import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../../context/ThemeContext';
import { useAit } from '../../../context/AitContext';
import {
  ArrowLeft, Building2, Plus, Upload, Trash2, ChevronRight, ChevronDown, ChevronUp,
  CheckCircle2, AlertTriangle, XCircle, Loader2, FileSpreadsheet, Pencil, X, Save,
  Sparkles, Users, FileText, Download, ExternalLink, MoreVertical, TrendingUp, Clock, Info
} from 'lucide-react';
import toast from 'react-hot-toast';
import { API_URL_BASE } from '../../../utils/config';

const RAILS_ROOT = API_URL_BASE.replace(/\/api\/v1\/?$/, '');
import US_STATES from '../../../constants/usStates';
import AddressAutocomplete from '../../common/AddressAutocomplete';
import AitChatDrawer from './AitChatDrawer';
import AitLeasingReport from './AitLeasingReport';
import AitPropertyStats from './AitPropertyStats';
import AitPropertyMembersModal from './AitPropertyMembersModal';
import AitInviteMemberModal from './AitInviteMemberModal';
import AitPropertyUnitFilter from './AitPropertyUnitFilter';
import AitPropertyAnalytics from './AitPropertyAnalytics';
import AitHistoryTimeline from './AitHistoryTimeline';
import AitGlossaryModal from './AitGlossaryModal';

const PROGRAM_OPTIONS = [
  { value: 'tax_credit', label: 'Tax Credit' },
  { value: 'bonds', label: 'Tax Exempt Bonds' },
  { value: 'home', label: 'HOME' },
  { value: 'hud', label: 'HUD' },
  { value: 'rd', label: 'Rural Development' },
  { value: 'lhtf', label: 'LHTF' },
  { value: 'arpa', label: 'ARPA' },
  { value: 'psvs', label: 'PSVS' },
  { value: 'fwhg', label: 'FWHG' },
  { value: 'sail', label: 'SAIL' },
  { value: 'mmrb', label: 'MMRB' },
  { value: 'other', label: 'Other' },
];

const PROPERTY_TYPE_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'new_construction', label: 'New Construction' },
  { value: 'acquisition_rehab', label: 'Acquisition / Rehab' },
  { value: 'resyndication', label: 'Re-syndication' },
];

const MSA_OPTIONS = [
  { value: 'average_income', label: 'Average Income' },
  { value: '40_60', label: '40/60' },
  { value: '20_50', label: '20/50' },
  { value: '25_60', label: '25/60 (NYC)' },
];

function formatUploadError(filename, rawError) {
  const err = (rawError || '').toLowerCase();

  if (err.includes('unsupported file type') || err.includes('expected excel')) {
    return {
      title: 'Unsupported File Format',
      message: `"${filename}" is not a supported spreadsheet format.`,
      suggestions: ['Upload an Excel file (.xlsx, .xls), CSV (.csv), or PDF rent roll', 'Make sure the file is not corrupted and try again'],
    };
  }
  if (err.includes('empty')) {
    return {
      title: 'Empty File',
      message: `"${filename}" appears to be empty or contains no data.`,
      suggestions: ['Make sure the file has at least one sheet with unit data', 'Check that the file is not corrupted or password-protected'],
    };
  }
  if (err.includes('column mapping') || err.includes('no response from claude')) {
    return {
      title: 'Could Not Identify Columns',
      message: `We couldn't automatically detect the column layout in "${filename}".`,
      suggestions: [
        'Make sure the spreadsheet has a clear header row with column names (e.g., "Unit #", "BR", "BIN", "Designation")',
        'The first sheet should contain the unit data',
        'Remove any merged cells or complex formatting in the header row',
      ],
    };
  }
  if (err.includes('anthropic') || err.includes('api_key')) {
    return {
      title: 'Service Temporarily Unavailable',
      message: 'The parsing service is temporarily unavailable. Please try again in a moment.',
      suggestions: ['Wait a minute and try uploading again', 'If the issue persists, contact support'],
    };
  }
  // Fallback
  return {
    title: 'Upload Failed',
    message: `Something went wrong while processing "${filename}".`,
    suggestions: [
      'Check that the file is a valid Excel spreadsheet with unit data',
      'Make sure the spreadsheet has clear column headers',
      'Try re-saving the file as .xlsx and uploading again',
    ],
    details: rawError,
  };
}

function AitPropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const {
    fetchProperty, updateProperty, createBuilding, updateBuilding, deleteBuilding, parseUpload, parseTicBatch, saveUpload, deleteUpload, importUnits
  } = useAit();

  const [property, setProperty] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuildingForm, setShowBuildingForm] = useState(false);
  const [buildingForm, setBuildingForm] = useState({ bin_number: '', name: '', total_units: '' });
  const [glossaryTopic, setGlossaryTopic] = useState(null);

  // Upload state — two-step: stage files, then parse, then import
  const [stagedFiles, setStagedFiles] = useState([]);    // File objects waiting to be parsed
  const [uploading, setUploading] = useState(false);
  const [parsedFiles, setParsedFiles] = useState([]);    // array of parsed results
  const [importing, setImporting] = useState(false);
  const [expandedBins, setExpandedBins] = useState({});
  const [showColumnMapping, setShowColumnMapping] = useState(false);

  // TIC upload state
  const [uploadTab, setUploadTab] = useState('rent_roll'); // 'rent_roll' | 'tic'
  const [ticStagedFiles, setTicStagedFiles] = useState([]);
  const [ticUploading, setTicUploading] = useState(false);
  const [ticParsedResults, setTicParsedResults] = useState([]);
  const [ticImporting, setTicImporting] = useState(false);

  // Edit state
  const [showEditForm, setShowEditForm] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ basic: true });
  const [uploadError, setUploadError] = useState(null); // { title, message, details, suggestions }

  // Chat state
  const [showChat, setShowChat] = useState(false);

  // Members state
  const [showMembers, setShowMembers] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [showLeasingReport, setShowLeasingReport] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [editBuilding, setEditBuilding] = useState(null);
  const [editBuildingForm, setEditBuildingForm] = useState({ bin_number: '', name: '', placed_in_service_date: '' });

  const isDark = theme === 'dark';
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'}`;
  const labelCls = 'block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300';

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    const data = await fetchProperty(id);
    if (data) {
      setProperty(data.property);
      setBuildings(data.buildings || []);
      setUploads(data.uploads || []);
    }
    setLoading(false);
  };

  const openEditForm = () => {
    setEditForm({
      name: property.name || '',
      address: property.address || '',
      city: property.city || '',
      state: property.state || '',
      zip_code: property.zip_code || '',
      county: property.county || '',
      compliance_contact_name: property.compliance_contact_name || '',
      compliance_contact_email: property.compliance_contact_email || '',
      total_units: property.total_units || '',
      restricted_units: property.restricted_units || '',
      number_of_bins: property.number_of_bins || '',
      placed_in_service_date: property.placed_in_service_date || '',
      property_type: property.property_type || '',
      utility_allowance_date: property.utility_allowance_date || '',
      owner_pays_utilities: property.owner_pays_utilities || false,
      gross_rent_floor_election: property.gross_rent_floor_election || false,
      gross_rent_floor_date: property.gross_rent_floor_date || '',
      msa_election: property.msa_election || 'average_income',
      non_metro_income_limits: property.non_metro_income_limits || false,
      programs: property.programs || [],
      tax_credit_percentage: property.tax_credit_percentage || '',
      bond_percentage: property.bond_percentage || '',
      home_high_count: property.home_high_count || '',
      home_low_count: property.home_low_count || '',
      home_unit_type: property.home_unit_type || '',
      elderly_housing: property.elderly_housing || false,
      elderly_age_requirement: property.elderly_age_requirement || '',
      all_members_elderly: property.all_members_elderly || false,
      state_set_asides: property.state_set_asides || '',
      lura_on_file: property.lura_on_file || false,
      acquisition_date: property.acquisition_date || '',
      rehab_date: property.rehab_date || '',
      new_construction: property.new_construction || false,
      authority_name: property.authority_name || '',
      authority_title: property.authority_title || '',
      authority_effective_date: property.authority_effective_date || '',
    });
    setExpandedSections({ basic: true });
    setShowEditForm(true);
  };

  const handleSaveProperty = async (e) => {
    e.preventDefault();
    setSaving(true);
    const result = await updateProperty(id, editForm);
    if (result.success) {
      toast.success('Property updated');
      setProperty(result.property);
      setShowEditForm(false);
    } else {
      toast.error(result.error || 'Failed to update property');
    }
    setSaving(false);
  };

  const toggleSection = (key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleProgram = (program) => {
    setEditForm(prev => {
      const current = prev.programs || [];
      const updated = current.includes(program)
        ? current.filter(p => p !== program)
        : [...current, program];
      return { ...prev, programs: updated };
    });
  };

  const handleCreateBuilding = async (e) => {
    e.preventDefault();
    const result = await createBuilding(id, buildingForm);
    if (result.success) {
      toast.success('Building added');
      setShowBuildingForm(false);
      setBuildingForm({ bin_number: '', name: '', total_units: '' });
      loadData();
    } else {
      toast.error(result.error || 'Failed to create building');
    }
  };

  const handleDeleteBuilding = async (buildingId) => {
    if (!confirm('Delete this building and all its units?')) return;
    const result = await deleteBuilding(id, buildingId);
    if (result.success) {
      toast.success('Building deleted');
      setBuildings(prev => prev.filter(b => b.id !== buildingId));
    } else {
      toast.error(result.error || 'Failed to delete');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';
    setStagedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveStagedFile = (idx) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleParseFiles = async () => {
    if (stagedFiles.length === 0) return;
    setUploading(true);

    for (const file of stagedFiles) {
      try {
        const result = await parseUpload(file);
        if (result.success) {
          setParsedFiles(prev => [...prev, { ...result, _filename: file.name }]);
          await saveUpload(id, result, file.name, 'rent_roll');
          toast.success(`Parsed ${result.units?.length || 0} units from ${file.name}`);
        } else {
          setUploadError(formatUploadError(file.name, result.error));
        }
      } catch (err) {
        setUploadError(formatUploadError(file.name, err.message));
      }
    }

    setStagedFiles([]);
    setUploading(false);
  };

  const handleRemoveParsedFile = (idx) => {
    setParsedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // Combine all units from all parsed files
  const allParsedUnits = parsedFiles.flatMap(f => f.units || []);
  const allColumnMappings = parsedFiles.length > 0 ? parsedFiles[parsedFiles.length - 1].column_mapping || {} : {};
  const allUnitsByBin = {};
  allParsedUnits.forEach(u => {
    const bin = u.bin_number || 'unknown';
    if (!allUnitsByBin[bin]) allUnitsByBin[bin] = [];
    allUnitsByBin[bin].push(u);
  });
  const allBinKeys = Object.keys(allUnitsByBin).sort();
  const combinedValidation = parsedFiles.length > 0 ? parsedFiles[parsedFiles.length - 1].validation : null;

  const handleImportAll = async () => {
    if (allParsedUnits.length === 0) return;
    setImporting(true);
    const result = await importUnits(id, allParsedUnits);
    if (result.success) {
      toast.success(`Imported ${result.units_imported} units into ${result.buildings_created + result.buildings_updated} buildings`);
      if (result.skipped_no_bin > 0) toast.error(`${result.skipped_no_bin} unit(s) skipped — missing BIN number`);
      setParsedFiles([]);
      loadData();
    } else {
      toast.error(result.error || 'Import failed');
    }
    setImporting(false);
  };

  const toggleBin = (bin) => {
    setExpandedBins(prev => ({ ...prev, [bin]: !prev[bin] }));
  };

  // --- TIC handlers ---
  const handleTicFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';
    setTicStagedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveTicFile = (idx) => {
    setTicStagedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleParseTics = async () => {
    if (ticStagedFiles.length === 0) return;
    setTicUploading(true);

    const result = await parseTicBatch(ticStagedFiles);
    if (result.results) {
      const successful = result.results.filter(r => r.success);
      const failed = result.results.filter(r => !r.success);
      setTicParsedResults(prev => [...prev, ...result.results]);
      if (successful.length > 0) toast.success(`Parsed ${successful.length} TIC${successful.length > 1 ? 's' : ''}`);
      if (failed.length > 0) toast.error(`${failed.length} TIC${failed.length > 1 ? 's' : ''} failed to parse`);
      // Save each successful TIC as an upload record
      for (const r of successful) {
        await saveUpload(id, r, r.filename, 'tic');
      }
    } else {
      toast.error(result.error || 'Failed to parse TICs');
    }

    setTicStagedFiles([]);
    setTicUploading(false);
  };

  const handleImportTics = async () => {
    const successResults = ticParsedResults.filter(r => r.success && r.unit_data);
    if (successResults.length === 0) return;
    setTicImporting(true);

    // Convert TIC unit_data objects into the same format import_units expects
    const units = successResults.map(r => r.unit_data);
    const result = await importUnits(id, units);
    if (result.success) {
      toast.success(`Imported ${result.units_imported} units from ${successResults.length} TICs`);
      if (result.skipped_no_bin > 0) toast.error(`${result.skipped_no_bin} unit(s) skipped — missing BIN number`);
      setTicParsedResults([]);
      loadData();
    } else {
      toast.error(result.error || 'Import failed');
    }
    setTicImporting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-10 text-center">
        <p>Property not found.</p>
        <button onClick={() => navigate('/dashboard/ait')} className="text-blue-500 mt-2">Back to properties</button>
      </div>
    );
  }

  const validation = combinedValidation;
  const programs = property.programs || [];
  const canEdit = property.user_role === 'owner' || property.user_role === 'editor';

  // Section toggle component
  const SectionHeader = ({ sectionKey, title }) => (
    <button
      type="button"
      onClick={() => toggleSection(sectionKey)}
      className={`w-full flex items-center justify-between py-2 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
    >
      {title}
      {expandedSections[sectionKey] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
    </button>
  );

  // Toggle switch component
  const Toggle = ({ checked, onChange, label }) => (
    <label className="flex items-center gap-2 cursor-pointer">
      <div className="relative">
        <input type="checkbox" className="sr-only" checked={checked} onChange={onChange} />
        <div className={`w-9 h-5 rounded-full transition-colors ${checked ? 'bg-blue-600' : isDark ? 'bg-gray-600' : 'bg-gray-300'}`} />
        <div className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </div>
      <span className="text-xs text-gray-700 dark:text-gray-300">{label}</span>
    </label>
  );

  return (
    <div className="h-full overflow-y-auto">
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Back + Header */}
      <button onClick={() => navigate('/dashboard/ait')} className={`flex items-center gap-1 text-sm mb-4 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}>
        <ArrowLeft size={16} /> Properties
      </button>

      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-dm-serif text-gray-900 dark:text-white">{property.name}</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowChat(true)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
              <Sparkles size={12} /> JPRO Assistant
            </button>
            <button onClick={() => setShowMembers(true)}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
              <Users size={12} /> Members
            </button>
            <div className="relative">
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                <MoreVertical size={12} />
              </button>
              {showMoreMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMoreMenu(false)} />
                  <div className={`absolute right-0 mt-1 w-48 rounded-lg shadow-lg border z-50 py-1 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <button
                      onClick={() => { setShowDocuments(true); setShowMoreMenu(false); }}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-left text-sm ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                    >
                      <FileText size={14} /> Documents
                      {uploads.length > 0 && <span className="ml-auto px-1.5 py-0.5 rounded-full bg-blue-500 text-white text-[10px] leading-none">{uploads.length}</span>}
                    </button>
                    <button
                      onClick={() => { setShowLeasingReport(true); setShowMoreMenu(false); }}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-left text-sm ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                    >
                      <Download size={14} /> Leasing Report
                    </button>
                  </div>
                </>
              )}
            </div>
            {canEdit && (
              <button onClick={openEditForm}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                <Pencil size={12} /> Edit
              </button>
            )}
          </div>
        </div>
        {property.address && (
          <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {property.address}{property.city ? `, ${property.city}` : ''}{property.state ? ` ${property.state}` : ''} {property.zip_code}
          </p>
        )}
        {/* Property detail chips */}
        <div className={`flex flex-wrap gap-2 mt-2`}>
          {property.total_units && (
            <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
              {property.total_units} units
            </span>
          )}
          {property.msa_election && (
            <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
              {property.msa_election.replace('_', '/')}
            </span>
          )}
          {property.property_type && (
            <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
              {property.property_type.replace(/_/g, ' ')}
            </span>
          )}
          {programs.length > 0 && programs.map(p => (
            <span key={p} className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
              {PROGRAM_OPTIONS.find(o => o.value === p)?.label || p}
            </span>
          ))}
          {property.elderly_housing && (
            <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-purple-900/30 text-purple-400' : 'bg-purple-50 text-purple-600'}`}>
              Elderly ({property.elderly_age_requirement}+)
            </span>
          )}
          {property.utility_allowance_date && (
            <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
              UA: {property.utility_allowance_date}
            </span>
          )}
          {property.owner_pays_utilities && (
            <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-600'}`}>
              Owner pays utilities
            </span>
          )}
          {property.user_role && property.user_role !== 'owner' && (
            <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-orange-900/30 text-orange-400' : 'bg-orange-50 text-orange-600'}`}>
              {property.user_role}
            </span>
          )}
        </div>
        {property.authority_name && (
          <div className={`flex items-center gap-2 mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <span className="font-medium">Delegation of Authority:</span>
            <span>{property.authority_name}{property.authority_title ? `, ${property.authority_title}` : ''}</span>
            {property.authority_effective_date && <span>(effective {property.authority_effective_date})</span>}
          </div>
        )}
      </div>

      {/* Tab Bar */}
      <div className={`flex gap-1 mb-5 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        {[
          { key: 'overview', label: 'Overview', icon: Building2 },
          { key: 'analytics', label: 'Property Analytics', icon: TrendingUp },
          { key: 'history', label: 'History', icon: Clock },
        ].map(tab => {
          const TabIcon = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                active
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : `border-transparent ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`
              }`}
            >
              <TabIcon size={14} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && <AitPropertyAnalytics propertyId={id} />}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className={`rounded-xl border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-white'}`}>
          <AitHistoryTimeline scope="property" ids={{ propertyId: id }} showUnit showBuilding />
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && <>

      {/* Buildings */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Buildings</h2>
          {canEdit && (
            <button onClick={() => setShowBuildingForm(true)}
              className="flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-600">
              <Plus size={14} /> Add Building
            </button>
          )}
        </div>

        {buildings.length === 0 ? (
          <div className={`text-center py-10 rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
            <Building2 size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No buildings yet. Add a building or upload a rent roll.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[420px] overflow-y-auto pr-1">
            {buildings.map(b => {
              const snap = b.calculation;
              const avgColor = !snap ? '' :
                snap.compliance_position?.startsWith('noncompliant') ? 'text-red-500' :
                snap.compliance_position?.startsWith('at_risk') ? 'text-orange-500' :
                snap.average_income > 57 ? 'text-orange-500' : 'text-green-500';
              const flexColor = !snap ? '' :
                snap.flexibility_index === 'high' ? 'text-green-500' :
                snap.flexibility_index === 'moderate' ? 'text-blue-500' :
                snap.flexibility_index === 'constrained' ? 'text-orange-500' : 'text-red-500';
              const pressureColor = !snap ? '' :
                snap.pressure_level === 'low' ? 'text-green-500' :
                snap.pressure_level === 'moderate' ? 'text-orange-500' :
                snap.pressure_level === 'elevated' ? 'text-orange-500' : 'text-red-500';
              const hasRisks = snap?.concentration_risks?.length > 0;
              return (
                <div key={b.id}
                  className={`group p-3 rounded-xl border cursor-pointer transition-all hover:shadow-sm ${
                    isDark ? 'bg-gray-800 border-gray-700 hover:border-gray-600' : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => navigate(`/dashboard/ait/${id}/buildings/${b.id}`)}
                >
                  {/* Header row */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-sm truncate block text-gray-900 dark:text-white">{b.name || b.bin_number}</span>
                      <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {b.unit_count || 0} units{b.bin_number && b.name ? ` · ${b.bin_number}` : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {snap && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setGlossaryTopic('flexibility-pressure'); }}
                          className={`p-1 rounded transition ${isDark ? 'text-gray-500 hover:text-blue-400 hover:bg-gray-700' : 'text-gray-400 hover:text-blue-500 hover:bg-gray-100'}`}
                          aria-label="What do flexibility and pressure mean?"
                          title="What do flexibility and pressure mean?"
                        >
                          <Info size={13} />
                        </button>
                      )}
                      {canEdit && (
                        <>
                          <button onClick={(e) => {
                              e.stopPropagation();
                              setEditBuilding(b);
                              setEditBuildingForm({ bin_number: b.bin_number || '', name: b.name || '', placed_in_service_date: b.placed_in_service_date || '' });
                            }}
                            className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-400 hover:text-blue-500">
                            <Pencil size={13} />
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleDeleteBuilding(b.id); }}
                            className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 dark:hover:bg-red-900/30 text-red-400 hover:text-red-500">
                            <Trash2 size={13} />
                          </button>
                        </>
                      )}
                      <ChevronRight size={14} className={isDark ? 'text-gray-600' : 'text-gray-300'} />
                    </div>
                  </div>

                  {snap ? (
                    <>
                      {/* Compliance stats row */}
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        <div>
                          <div className={`text-lg font-semibold leading-tight ${avgColor}`}>{snap.average_income != null ? `${snap.average_income}%` : '—'}</div>
                          <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>avg income</div>
                        </div>
                        <div>
                          <div className={`text-xs font-medium capitalize leading-tight mt-0.5 ${flexColor}`}>{snap.flexibility_index || '—'}</div>
                          <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>flexibility</div>
                        </div>
                        <div>
                          <div className={`text-xs font-medium capitalize leading-tight mt-0.5 ${pressureColor}`}>{snap.pressure_level || '—'}</div>
                          <div className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>pressure</div>
                        </div>
                      </div>

                      {/* Occupancy row */}
                      <div className={`flex items-center gap-3 text-[10px] mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span className="text-green-500 font-medium">{snap.occupied_count || 0} occ</span>
                        {(snap.leased_count || 0) > 0 && <span className="text-blue-500 font-medium">{snap.leased_count} leased</span>}
                        {(snap.notice_count || 0) > 0 && <span className="text-orange-500 font-medium">{snap.notice_count} notice</span>}
                        <span className={`font-medium ${(snap.vacant_count || 0) > 0 ? 'text-orange-500' : ''}`}>{snap.vacant_count || 0} vac</span>
                        <span>{snap.occupancy_percentage || 0}%</span>
                        {(snap.market_rate_count || 0) > 0 && <span>{snap.market_rate_count} mkt</span>}
                      </div>

                      {/* Designation mini-bars */}
                      {snap.designation_distribution && Object.keys(snap.designation_distribution).length > 0 && (
                        <div className="flex gap-1 flex-wrap mb-2">
                          {Object.entries(snap.designation_distribution).sort(([a],[b]) => Number(a) - Number(b)).map(([d, c]) => (
                            <span key={d} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                              {d}%:{c}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Bottom row: qualified + risks */}
                      <div className={`flex items-center justify-between pt-2 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {snap.qualified_count || 0} qualified{snap.qualified_percentage ? ` (${snap.qualified_percentage}%)` : ''}
                        </span>
                        {hasRisks && (
                          <span className="flex items-center gap-1 text-xs text-orange-500">
                            <AlertTriangle size={11} /> {snap.concentration_risks.length} risk{snap.concentration_risks.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className={`text-xs py-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No qualified units yet</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Property-level Unit Filter */}
      <AitPropertyUnitFilter buildings={buildings} propertyId={id} />

      {/* Property Stats */}
      <AitPropertyStats buildings={buildings} />

      {/* Upload Section */}
      {canEdit && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Upload Data</h2>
            {/* Tab switcher */}
            <div className={`flex rounded-lg border text-xs ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
              <button
                onClick={() => setUploadTab('rent_roll')}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-l-lg transition-colors ${
                  uploadTab === 'rent_roll'
                    ? 'bg-blue-600 text-white'
                    : isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <FileSpreadsheet size={12} /> Rent Roll
              </button>
              <button
                onClick={() => setUploadTab('tic')}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-r-lg border-l transition-colors ${
                  isDark ? 'border-gray-600' : 'border-gray-300'
                } ${
                  uploadTab === 'tic'
                    ? 'bg-blue-600 text-white'
                    : isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <FileText size={12} /> TIC Documents
              </button>
            </div>
            {uploadTab === 'rent_roll' && (
              <label className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border cursor-pointer transition-colors ${
                isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}>
                <Upload size={13} />
                {stagedFiles.length > 0 ? 'Add more files' : 'Upload'}
                <input type="file" className="hidden" accept=".xlsx,.xls,.csv,.pdf" multiple
                  onChange={handleFileSelect} />
              </label>
            )}
            {uploadTab === 'tic' && (
              <label className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border cursor-pointer transition-colors ${
                isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
              }`}>
                <Upload size={13} />
                {ticStagedFiles.length > 0 ? 'Add more' : 'Upload PDFs'}
                <input type="file" className="hidden" accept=".pdf" multiple
                  onChange={handleTicFileSelect} />
              </label>
            )}
          </div>

          {/* ===== RENT ROLL TAB ===== */}
          {uploadTab === 'rent_roll' && (<>


          {/* Uploading state */}
          {uploading && (
            <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <Loader2 className="animate-spin text-blue-500 shrink-0" size={16} />
              <div>
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>JPRO Parsing...</span>
                <span className={`text-xs block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>This may take a moment for large files</span>
              </div>
            </div>
          )}

          {/* Staged files */}
          {!uploading && stagedFiles.length > 0 && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {stagedFiles.map((file, idx) => (
                  <div key={idx} className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                    <FileSpreadsheet size={12} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{file.name}</span>
                    <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>
                      ({(file.size / 1024).toFixed(0)} KB)
                    </span>
                    <button onClick={() => handleRemoveStagedFile(idx)}
                      className={`p-0.5 rounded ${isDark ? 'hover:bg-gray-700 text-gray-600' : 'hover:bg-gray-200 text-gray-400'}`}>
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={handleParseFiles}
                className="flex items-center gap-2 px-4 py-2 mx-auto bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                <FileSpreadsheet size={14} />
                Parse {stagedFiles.length} file{stagedFiles.length > 1 ? 's' : ''} with AI
              </button>
            </div>
          )}

          {/* Upload results panel */}
          {parsedFiles.length > 0 && (
              <div className={`mt-4 rounded-xl border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                {/* A. Status header */}
                <div className={`px-4 py-3 flex items-center justify-between ${
                  validation?.status === 'ready' ? 'bg-green-50 dark:bg-green-900/20' :
                  validation?.status === 'needs_designation' ? 'bg-blue-50 dark:bg-blue-900/20' :
                  validation?.status === 'review' ? 'bg-orange-50 dark:bg-orange-900/20' :
                  'bg-red-50 dark:bg-red-900/20'
                }`}>
                  <div className="flex items-center gap-2">
                    {validation?.status === 'ready' ? <CheckCircle2 size={16} className="text-green-600 dark:text-green-400" /> :
                     validation?.status === 'needs_designation' ? <FileSpreadsheet size={16} className="text-blue-600 dark:text-blue-400" /> :
                     validation?.status === 'review' ? <AlertTriangle size={16} className="text-orange-600 dark:text-orange-400" /> :
                     <XCircle size={16} className="text-red-600 dark:text-red-400" />}
                    <span className={`text-sm font-medium ${
                      validation?.status === 'ready' ? 'text-green-700 dark:text-green-400' :
                      validation?.status === 'needs_designation' ? 'text-blue-700 dark:text-blue-400' :
                      validation?.status === 'review' ? 'text-orange-700 dark:text-orange-400' :
                      'text-red-700 dark:text-red-400'
                    }`}>
                      {validation?.status_message}
                    </span>
                  </div>
                  <button onClick={() => setParsedFiles([])}
                    className={`p-1 rounded ${isDark ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}>
                    <X size={14} />
                  </button>
                </div>

                <div className={`p-4 space-y-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  {/* Summary chips */}
                  <div className="flex flex-wrap gap-2">
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                      {allParsedUnits.length} units parsed
                    </span>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                      {allBinKeys.length} buildings detected
                    </span>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                      {parsedFiles.length} file{parsedFiles.length > 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Parsed files list */}
                  {parsedFiles.length > 0 && (
                    <div>
                      <p className={`text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Uploaded Files</p>
                      <div className="space-y-1">
                        {parsedFiles.map((pf, idx) => (
                          <div key={idx} className={`flex items-center justify-between px-3 py-1.5 rounded-lg text-xs ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
                            <div className="flex items-center gap-2">
                              <FileSpreadsheet size={12} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                              <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{pf._filename}</span>
                              <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>
                                {(pf.units || []).length} units · {(pf.detected_bins || []).length} BINs
                              </span>
                            </div>
                            <button onClick={() => handleRemoveParsedFile(idx)}
                              className={`p-0.5 rounded ${isDark ? 'hover:bg-gray-700 text-gray-600' : 'hover:bg-gray-200 text-gray-400'}`}>
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* B. Column mapping (collapsed by default) */}
                  {Object.keys(allColumnMappings).length > 0 && (
                    <div>
                      <button
                        onClick={() => setShowColumnMapping(prev => !prev)}
                        className={`flex items-center justify-between w-full text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        <span>Column Mapping ({Object.keys(allColumnMappings).length} fields)</span>
                        {showColumnMapping ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      </button>
                      {showColumnMapping && <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className={isDark ? 'bg-gray-900' : 'bg-gray-50'}>
                              <th className={`text-left px-3 py-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Spreadsheet Column</th>
                              <th className={`text-left px-3 py-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Mapped To</th>
                              <th className={`text-left px-3 py-1.5 font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Confidence</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(allColumnMappings).map(([field, info]) => (
                              <tr key={field} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                <td className={`px-3 py-1.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>"{info.header}"</td>
                                <td className={`px-3 py-1.5 font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{field}</td>
                                <td className="px-3 py-1.5">
                                  <span className={`inline-flex items-center gap-1 ${
                                    info.confidence === 'high' ? 'text-green-600 dark:text-green-400' :
                                    info.confidence === 'medium' ? 'text-orange-600 dark:text-orange-400' :
                                    'text-red-500'
                                  }`}>
                                    {info.confidence === 'high' ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
                                    {info.confidence}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>}
                    </div>
                  )}

                  {/* C. BIN breakdown */}
                  {allBinKeys.length > 0 && (
                    <div>
                      <p className={`text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Buildings</p>
                      <div className={`rounded-lg border divide-y ${isDark ? 'border-gray-700 divide-gray-700' : 'border-gray-200 divide-gray-100'}`}>
                        {allBinKeys.map(bin => {
                          const binUnits = allUnitsByBin[bin];
                          const designations = binUnits.map(u => u.current_designation).filter(Boolean);
                          const uniqueDesigs = [...new Set(designations)].sort((a, b) => a - b);
                          const desigSummary = uniqueDesigs.length === 0
                            ? 'no designations'
                            : uniqueDesigs.length === 1
                              ? `all ${uniqueDesigs[0]}% AMI`
                              : `${uniqueDesigs.map(d => d + '%').join(', ')} AMI`;
                          const isExpanded = expandedBins[bin];

                          return (
                            <div key={bin}>
                              <button
                                onClick={() => toggleBin(bin)}
                                className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left ${
                                  isDark ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <Building2 size={12} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                                  <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>BIN {bin}</span>
                                  <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>
                                    · {binUnits.length} units · {desigSummary}
                                  </span>
                                </div>
                                {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                              </button>
                              {isExpanded && (
                                <div className={`px-3 pb-2 ${isDark ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className={isDark ? 'text-gray-500' : 'text-gray-400'}>
                                        <th className="text-left py-1 px-1">Unit</th>
                                        <th className="text-left py-1 px-1">BR</th>
                                        <th className="text-left py-1 px-1">Designation</th>
                                        <th className="text-left py-1 px-1">Qualified</th>
                                        <th className="text-center py-1 px-1">Mgr</th>
                                        <th className="text-left py-1 px-1">Programs</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {binUnits.slice(0, 10).map((u, idx) => (
                                        <tr key={idx} className={`border-t ${isDark ? 'border-gray-800 text-gray-300' : 'border-gray-100 text-gray-700'}`}>
                                          <td className="py-1 px-1 font-mono">{u.unit_number}</td>
                                          <td className="py-1 px-1">{u.bedroom_count ?? '—'}</td>
                                          <td className="py-1 px-1">{u.current_designation ? `${u.current_designation}%` : '—'}</td>
                                          <td className="py-1 px-1">{u.qualified !== false ? 'Yes' : 'No'}</td>
                                          <td className="py-1 px-1 text-center">{u.is_manager_unit ? <span className="text-purple-600 dark:text-purple-400 font-bold">Mgr</span> : '—'}</td>
                                          <td className="py-1 px-1">{u.program_flags ? Object.keys(u.program_flags).filter(k => u.program_flags[k]).join(', ') : '—'}</td>
                                        </tr>
                                      ))}
                                      {binUnits.length > 10 && (
                                        <tr><td colSpan={6} className={`py-1 px-1 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                                          ... and {binUnits.length - 10} more units
                                        </td></tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* D. Validation issues */}
                  {(validation?.missing_required?.length > 0 || validation?.unit_issues?.length > 0 || validation?.low_confidence_mappings?.length > 0) && (
                    <div>
                      <p className={`text-xs font-medium mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Issues</p>
                      <div className="space-y-1">
                        {validation?.missing_required?.map((m, i) => (
                          <div key={`req-${i}`} className={`flex items-start gap-1.5 text-xs p-2 rounded-lg ${isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'}`}>
                            <XCircle size={12} className="shrink-0 mt-0.5" />
                            <span>Missing required: <strong>{m.field}</strong> — {m.message}</span>
                          </div>
                        ))}
                        {validation?.unit_issues?.map((issue, i) => (
                          <div key={`iss-${i}`} className={`flex items-start gap-1.5 text-xs p-2 rounded-lg ${
                            issue.severity === 'error' ? isDark ? 'text-red-400' : ' text-red-600' :
                            issue.severity === 'warning' ? isDark ? 'text-orange-400' : ' text-orange-700' :
                            isDark ? ' text-blue-400' : ' text-blue-600'
                          }`}>
                            {issue.severity === 'error' ? <XCircle size={12} className="shrink-0 mt-0.5" /> :
                             issue.severity === 'warning' ? <AlertTriangle size={12} className="shrink-0 mt-0.5" /> :
                             <FileSpreadsheet size={12} className="shrink-0 mt-0.5" />}
                            <span>{issue.message}</span>
                          </div>
                        ))}
                        {validation?.low_confidence_mappings?.map((lc, i) => (
                          <div key={`lc-${i}`} className={`flex items-start gap-1.5 text-xs p-2 rounded-lg ${isDark ? 'bg-orange-900/20 text-orange-400' : 'bg-orange-50 text-orange-700'}`}>
                            <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                            <span>{lc.message}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* E. AI notes — show from most recent file */}
                  {parsedFiles.length > 0 && parsedFiles[parsedFiles.length - 1].notes && (
                    <div className={`text-xs p-3 rounded-lg ${isDark ? 'bg-gray-900 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                      <span className="font-medium">JPRO Notes:</span> {parsedFiles[parsedFiles.length - 1].notes}
                    </div>
                  )}

                  {/* F. Import button */}
                  {allParsedUnits.length > 0 && validation?.status !== 'incomplete' && (
                    <div className={`pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <button
                        onClick={handleImportAll}
                        disabled={importing}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                      >
                        {importing ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Upload size={16} />
                            Import All ({allParsedUnits.length} units → {allBinKeys.length} buildings)
                          </>
                        )}
                      </button>
                      <button
                        onClick={async () => {
                          for (const pf of parsedFiles) {
                            await saveUpload(id, pf, pf.filename || 'rent_roll', 'rent_roll');
                          }
                          toast.success('Saved to Documents');
                          loadData();
                        }}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm transition-colors ${
                          isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <Save size={16} />
                        Save to Documents
                      </button>
                    </div>
                  )}
                </div>
              </div>
          )}

          {/* Rent roll empty state */}
          {!uploading && stagedFiles.length === 0 && parsedFiles.length === 0 && (
            <div className={`text-center py-6 rounded-lg border border-dashed ${isDark ? 'border-gray-700 text-gray-500' : 'border-gray-300 text-gray-400'}`}>
              <FileSpreadsheet size={24} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Upload rent rolls (.xlsx, .xls, .csv, .pdf)</p>
              <p className="text-xs mt-1">JPRO will extract unit, designation, and occupancy data automatically •	Different vendors format: Yardi, RealPage, Boston Post, custom, JPRO will handle it</p>
            </div>
          )}
          </>)}

          {/* ===== TIC TAB ===== */}
          {uploadTab === 'tic' && (<>
            {/* TIC uploading state */}
            {ticUploading && (
              <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <Loader2 className="animate-spin text-blue-500 shrink-0" size={16} />
                <div>
                  <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}> JPRO Parsing TIC documents...</span>
                  <span className={`text-xs block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Extracting tenant, income, and rent data</span>
                </div>
              </div>
            )}

            {/* TIC staged files */}
            {!ticUploading && ticStagedFiles.length > 0 && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {ticStagedFiles.map((file, idx) => (
                    <div key={idx} className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                      <FileText size={12} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                      <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{file.name}</span>
                      <span className={isDark ? 'text-gray-600' : 'text-gray-400'}>
                        ({(file.size / 1024).toFixed(0)} KB)
                      </span>
                      <button onClick={() => handleRemoveTicFile(idx)}
                        className={`p-0.5 rounded ${isDark ? 'hover:bg-gray-700 text-gray-600' : 'hover:bg-gray-200 text-gray-400'}`}>
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleParseTics}
                  className="flex items-center gap-2 px-4 py-2 mx-auto bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  <FileText size={14} />
                  Parse {ticStagedFiles.length} TIC{ticStagedFiles.length > 1 ? 's' : ''} with AI
                </button>
              </div>
            )}

            {/* TIC parsed results */}
            {ticParsedResults.length > 0 && (
              <div className={`mt-4 rounded-xl border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                {/* Header */}
                <div className="bg-green-50 dark:bg-green-900/20 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-green-700 dark:text-green-400">
                      {ticParsedResults.filter(r => r.success).length} of {ticParsedResults.length} TICs parsed successfully
                    </span>
                  </div>
                  <button onClick={() => setTicParsedResults([])}
                    className={`p-1 rounded ${isDark ? 'hover:bg-gray-700 text-gray-500' : 'hover:bg-gray-200 text-gray-400'}`}>
                    <X size={14} />
                  </button>
                </div>

                <div className={`p-4 space-y-2 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                  {ticParsedResults.map((tic, idx) => {
                    const d = tic.unit_data || {};
                    const v = tic.validation || {};
                    return (
                      <div key={idx} className={`p-3 rounded-lg border ${
                        !tic.success ? isDark ? 'border-red-800 bg-red-900/10' : 'border-red-200 bg-red-50' :
                        v.status === 'review' ? isDark ? 'border-orange-800 bg-orange-900/10' : 'border-orange-200 bg-orange-50' :
                        isDark ? 'border-gray-700' : 'border-gray-200'
                      }`}>
                        {!tic.success ? (
                          <div className="flex items-center gap-2">
                            <XCircle size={14} className="text-red-500 shrink-0" />
                            <span className="text-sm text-red-600 dark:text-red-400">{tic.filename}: {tic.error}</span>
                          </div>
                        ) : (
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                  Unit {d.unit_number || '?'}
                                </span>
                                {d.bin_number && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                    BIN {d.bin_number}
                                  </span>
                                )}
                                {d.current_designation && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium">
                                    {d.current_designation}% AMI
                                  </span>
                                )}
                                {d.cert_type && (
                                  <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                    ({d.cert_type})
                                  </span>
                                )}
                              </div>
                              <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{tic.filename}</span>
                            </div>
                            <div className={`flex flex-wrap gap-x-4 gap-y-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {d.bedroom_count != null && <span>{d.bedroom_count} BR</span>}
                              {d.household_size && <span>HH Size: {d.household_size}</span>}
                              {d.household_income && <span>Income: ${Number(d.household_income).toLocaleString()}</span>}
                              {d.rent_amount && <span>Rent: ${Number(d.rent_amount).toLocaleString()}</span>}
                              {d.utility_allowance && <span>UA: ${Number(d.utility_allowance).toLocaleString()}</span>}
                              {d.move_in_date && <span>Move-in: {d.move_in_date}</span>}
                            </div>
                            {v.issues?.length > 0 && (
                              <div className="space-y-0.5 mt-1">
                                {v.issues.map((issue, i) => (
                                  <div key={i} className={`text-xs flex items-start gap-1 ${
                                    issue.severity === 'error' ? 'text-red-500' :
                                    issue.severity === 'warning' ? 'text-orange-500' : 'text-blue-500'
                                  }`}>
                                    {issue.severity === 'error' ? <XCircle size={11} className="shrink-0 mt-0.5" /> :
                                     issue.severity === 'warning' ? <AlertTriangle size={11} className="shrink-0 mt-0.5" /> :
                                     <CheckCircle2 size={11} className="shrink-0 mt-0.5" />}
                                    <span>{issue.message}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Import button */}
                  {ticParsedResults.some(r => r.success) && (
                    <div className={`pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                      <button
                        onClick={handleImportTics}
                        disabled={ticImporting}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                      >
                        {ticImporting ? (
                          <><Loader2 size={16} className="animate-spin" /> Importing...</>
                        ) : (
                          <><Upload size={16} /> Import {ticParsedResults.filter(r => r.success).length} TIC{ticParsedResults.filter(r => r.success).length > 1 ? 's' : ''}</>
                        )}
                      </button>
                      <button
                        onClick={async () => {
                          for (const tic of ticParsedResults.filter(r => r.success)) {
                            await saveUpload(id, tic, tic.filename || 'tic_document', 'tic');
                          }
                          toast.success('TICs saved to Documents');
                          loadData();
                        }}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm ${
                          isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <Save size={16} /> Save to Documents
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* TIC empty state */}
            {!ticUploading && ticStagedFiles.length === 0 && ticParsedResults.length === 0 && (
              <div className={`text-center py-6 rounded-lg border border-dashed ${isDark ? 'border-gray-700 text-gray-500' : 'border-gray-300 text-gray-400'}`}>
                <FileText size={24} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Upload TIC (Tenant Income Certification) PDFs</p>
                <p className="text-xs mt-1">JPRO will extract unit, income, rent, and tenant data automatically •	Different vendors format: Yardi, RealPage, Boston Post, custom. JPRO will handle</p>
              </div>
            )}
          </>)}
        </div>
      )}

      </>}

      {/* Create building modal */}
      {showBuildingForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className={`w-full max-w-md rounded-xl p-6 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">Add Building</h2>
            <form onSubmit={handleCreateBuilding} className="space-y-3">
              <div>
                <label className={labelCls}>BIN Number *</label>
                <input type="text" required value={buildingForm.bin_number}
                  onChange={(e) => setBuildingForm(prev => ({ ...prev, bin_number: e.target.value }))}
                  className={inputCls}
                  placeholder="e.g., FL00-53705" />
              </div>
              <div>
                <label className={labelCls}>Display Name</label>
                <input type="text" value={buildingForm.name}
                  onChange={(e) => setBuildingForm(prev => ({ ...prev, name: e.target.value }))}
                  className={inputCls}
                  placeholder="e.g., Building A" />
              </div>
              <div>
                <label className={labelCls}>Total Units</label>
                <input type="number" value={buildingForm.total_units}
                  onChange={(e) => setBuildingForm(prev => ({ ...prev, total_units: e.target.value }))}
                  className={inputCls} />
              </div>
              <div className="flex justify-end gap-3 pt-3">
                <button type="button" onClick={() => setShowBuildingForm(false)}
                  className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}>
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
                  Add Building
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit property modal */}
      {showEditForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto">
          <div className={`w-full max-w-2xl rounded-xl p-6 my-8 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">Edit Property</h2>
              <button onClick={() => setShowEditForm(false)} className={`p-1 rounded ${isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}>
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSaveProperty} className="space-y-1">

              {/* Section 1: Basic Info */}
              <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <SectionHeader sectionKey="basic" title="Basic Info" />
                {expandedSections.basic && (
                  <div className="pb-4 space-y-3">
                    <div>
                      <label className={labelCls}>Property Name *</label>
                      <input type="text" required value={editForm.name}
                        onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                        className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>
                        Address <span className="text-[10px] text-gray-500 font-normal">— picking one fills City, State, ZIP</span>
                      </label>
                      <AddressAutocomplete
                        value={editForm.address || ''}
                        onChange={(val) => setEditForm(prev => ({ ...prev, address: val }))}
                        onPlaceSelect={(p) => setEditForm(prev => ({
                          ...prev,
                          address: p.address,
                          city: p.city || prev.city,
                          state: p.state || prev.state,
                          zip_code: p.zip_code || prev.zip_code,
                        }))}
                      />
                    </div>
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className={labelCls}>City</label>
                        <input type="text" value={editForm.city}
                          onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))}
                          className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>State</label>
                        <select value={editForm.state}
                          onChange={(e) => setEditForm(prev => ({ ...prev, state: e.target.value }))}
                          className={inputCls}>
                          <option value="">Select</option>
                          {US_STATES.map(s => (
                            <option key={s.value} value={s.value}>{s.value}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>ZIP</label>
                        <input type="text" value={editForm.zip_code}
                          onChange={(e) => setEditForm(prev => ({ ...prev, zip_code: e.target.value }))}
                          className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>County</label>
                        <input type="text" value={editForm.county}
                          onChange={(e) => setEditForm(prev => ({ ...prev, county: e.target.value }))}
                          className={inputCls} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Compliance Contact</label>
                        <input type="text" value={editForm.compliance_contact_name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, compliance_contact_name: e.target.value }))}
                          className={inputCls} placeholder="Name" />
                      </div>
                      <div>
                        <label className={labelCls}>Contact Email</label>
                        <input type="email" value={editForm.compliance_contact_email}
                          onChange={(e) => setEditForm(prev => ({ ...prev, compliance_contact_email: e.target.value }))}
                          className={inputCls} />
                      </div>
                    </div>
                    <div className={`text-xs font-medium uppercase tracking-wider mt-3 mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Delegation of Authority</div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className={labelCls}>Authority Name</label>
                        <input type="text" value={editForm.authority_name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, authority_name: e.target.value }))}
                          className={inputCls} placeholder="Name" />
                      </div>
                      <div>
                        <label className={labelCls}>Title</label>
                        <input type="text" value={editForm.authority_title}
                          onChange={(e) => setEditForm(prev => ({ ...prev, authority_title: e.target.value }))}
                          className={inputCls} placeholder="Title" />
                      </div>
                      <div>
                        <label className={labelCls}>Effective Date</label>
                        <input type="date" value={editForm.authority_effective_date}
                          onChange={(e) => setEditForm(prev => ({ ...prev, authority_effective_date: e.target.value }))}
                          className={inputCls} />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 2: Unit & Building Config */}
              <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <SectionHeader sectionKey="units" title="Unit & Building Config" />
                {expandedSections.units && (
                  <div className="pb-4 space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className={labelCls}>Total Units</label>
                        <input type="number" value={editForm.total_units}
                          onChange={(e) => setEditForm(prev => ({ ...prev, total_units: e.target.value }))}
                          className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Restricted Units</label>
                        <input type="number" value={editForm.restricted_units}
                          onChange={(e) => setEditForm(prev => ({ ...prev, restricted_units: e.target.value }))}
                          className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Number of BINs</label>
                        <input type="number" value={editForm.number_of_bins}
                          onChange={(e) => setEditForm(prev => ({ ...prev, number_of_bins: e.target.value }))}
                          className={inputCls} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Placed in Service Date</label>
                        <input type="date" value={editForm.placed_in_service_date}
                          onChange={(e) => setEditForm(prev => ({ ...prev, placed_in_service_date: e.target.value }))}
                          className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Property Type</label>
                        <select value={editForm.property_type}
                          onChange={(e) => setEditForm(prev => ({ ...prev, property_type: e.target.value }))}
                          className={inputCls}>
                          {PROPERTY_TYPE_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 3: Utility Allowance & Rent */}
              <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <SectionHeader sectionKey="utility" title="Utility Allowance & Rent" />
                {expandedSections.utility && (
                  <div className="pb-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>UA Effective Date</label>
                        <input type="date" value={editForm.utility_allowance_date}
                          onChange={(e) => setEditForm(prev => ({ ...prev, utility_allowance_date: e.target.value }))}
                          className={inputCls} />
                      </div>
                      <div className="flex items-end pb-2">
                        <Toggle
                          checked={editForm.owner_pays_utilities}
                          onChange={(e) => setEditForm(prev => ({ ...prev, owner_pays_utilities: e.target.checked }))}
                          label="Owner pays utilities"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Toggle
                        checked={editForm.gross_rent_floor_election}
                        onChange={(e) => setEditForm(prev => ({ ...prev, gross_rent_floor_election: e.target.checked }))}
                        label="Gross Rent Floor election made"
                      />
                      {editForm.gross_rent_floor_election && (
                        <div className="ml-11">
                          <label className={labelCls}>Election Date</label>
                          <input type="date" value={editForm.gross_rent_floor_date}
                            onChange={(e) => setEditForm(prev => ({ ...prev, gross_rent_floor_date: e.target.value }))}
                            className={`${inputCls} max-w-xs`} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Section 4: Set-Aside Elections */}
              <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <SectionHeader sectionKey="setaside" title="Set-Aside Elections" />
                {expandedSections.setaside && (
                  <div className="pb-4 space-y-3">
                    <div>
                      <label className={labelCls}>Federal Set-Aside</label>
                      <select value={editForm.msa_election}
                        onChange={(e) => setEditForm(prev => ({ ...prev, msa_election: e.target.value }))}
                        className={`${inputCls} max-w-xs`}>
                        {MSA_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                    <Toggle
                      checked={editForm.non_metro_income_limits}
                      onChange={(e) => setEditForm(prev => ({ ...prev, non_metro_income_limits: e.target.checked }))}
                      label="Qualifies for non-metro income limits"
                    />
                  </div>
                )}
              </div>

              {/* Section 5: Programs */}
              <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <SectionHeader sectionKey="programs" title="Programs" />
                {expandedSections.programs && (
                  <div className="pb-4 space-y-3">
                    <div className="flex flex-wrap gap-3">
                      {PROGRAM_OPTIONS.map(p => (
                        <label key={p.value} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox"
                            checked={(editForm.programs || []).includes(p.value)}
                            onChange={() => toggleProgram(p.value)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                          <span className="text-xs text-gray-700 dark:text-gray-300">{p.label}</span>
                        </label>
                      ))}
                    </div>

                    {/* Tax Credit sub-fields */}
                    {(editForm.programs || []).includes('tax_credit') && (
                      <div className="ml-6">
                        <label className={labelCls}>Tax Credit Percentage</label>
                        <input type="number" min="1" max="100" value={editForm.tax_credit_percentage}
                          onChange={(e) => setEditForm(prev => ({ ...prev, tax_credit_percentage: e.target.value }))}
                          className={`${inputCls} max-w-[120px]`} placeholder="100" />
                      </div>
                    )}

                    {/* Bonds sub-fields */}
                    {(editForm.programs || []).includes('bonds') && (
                      <div className="ml-6">
                        <label className={labelCls}>Bond Percentage</label>
                        <input type="number" min="1" max="100" value={editForm.bond_percentage}
                          onChange={(e) => setEditForm(prev => ({ ...prev, bond_percentage: e.target.value }))}
                          className={`${inputCls} max-w-[120px]`} placeholder="50" />
                      </div>
                    )}

                    {/* HOME sub-fields */}
                    {(editForm.programs || []).includes('home') && (
                      <div className="ml-6 space-y-2">
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className={labelCls}>HOME High Units</label>
                            <input type="number" value={editForm.home_high_count}
                              onChange={(e) => setEditForm(prev => ({ ...prev, home_high_count: e.target.value }))}
                              className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls}>HOME Low Units</label>
                            <input type="number" value={editForm.home_low_count}
                              onChange={(e) => setEditForm(prev => ({ ...prev, home_low_count: e.target.value }))}
                              className={inputCls} />
                          </div>
                          <div>
                            <label className={labelCls}>Unit Type</label>
                            <select value={editForm.home_unit_type}
                              onChange={(e) => setEditForm(prev => ({ ...prev, home_unit_type: e.target.value }))}
                              className={inputCls}>
                              <option value="">Select</option>
                              <option value="fixed">Fixed</option>
                              <option value="floating">Floating</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Section 6: Special Designations */}
              <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <SectionHeader sectionKey="designations" title="Special Designations" />
                {expandedSections.designations && (
                  <div className="pb-4 space-y-3">
                    <Toggle
                      checked={editForm.elderly_housing}
                      onChange={(e) => setEditForm(prev => ({ ...prev, elderly_housing: e.target.checked }))}
                      label="Elderly housing"
                    />
                    {editForm.elderly_housing && (
                      <div className="ml-11 space-y-2">
                        <div>
                          <label className={labelCls}>Age Requirement</label>
                          <select value={editForm.elderly_age_requirement}
                            onChange={(e) => setEditForm(prev => ({ ...prev, elderly_age_requirement: e.target.value }))}
                            className={`${inputCls} max-w-[120px]`}>
                            <option value="">Select</option>
                            <option value="55">55+</option>
                            <option value="62">62+</option>
                          </select>
                        </div>
                        <Toggle
                          checked={editForm.all_members_elderly}
                          onChange={(e) => setEditForm(prev => ({ ...prev, all_members_elderly: e.target.checked }))}
                          label="All household members must be elderly"
                        />
                      </div>
                    )}
                    <div>
                      <label className={labelCls}>State Set-Asides</label>
                      <textarea value={editForm.state_set_asides}
                        onChange={(e) => setEditForm(prev => ({ ...prev, state_set_asides: e.target.value }))}
                        rows={2}
                        className={inputCls}
                        placeholder="State-specific requirements..." />
                    </div>
                    <Toggle
                      checked={editForm.lura_on_file}
                      onChange={(e) => setEditForm(prev => ({ ...prev, lura_on_file: e.target.checked }))}
                      label="LURA / Regulatory Agreement on file"
                    />
                  </div>
                )}
              </div>

              {/* Section 7: Acquisition / Rehab */}
              <div>
                <SectionHeader sectionKey="acquisition" title="Acquisition / Rehab" />
                {expandedSections.acquisition && (
                  <div className="pb-4 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>Acquisition Date</label>
                        <input type="date" value={editForm.acquisition_date}
                          onChange={(e) => setEditForm(prev => ({ ...prev, acquisition_date: e.target.value }))}
                          className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Rehab Date</label>
                        <input type="date" value={editForm.rehab_date}
                          onChange={(e) => setEditForm(prev => ({ ...prev, rehab_date: e.target.value }))}
                          className={inputCls} />
                      </div>
                    </div>
                    <Toggle
                      checked={editForm.new_construction}
                      onChange={(e) => setEditForm(prev => ({ ...prev, new_construction: e.target.checked }))}
                      label="New construction"
                    />
                  </div>
                )}
              </div>

              {/* Save / Cancel */}
              <div className={`flex justify-end gap-3 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <button type="button" onClick={() => setShowEditForm(false)}
                  className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'}`}>
                  Cancel
                </button>
                <button type="submit" disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm disabled:opacity-50">
                  {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* AI Chat Drawer */}
      <AitChatDrawer
        propertyId={id}
        propertyName={property?.name}
        isOpen={showChat}
        onClose={() => setShowChat(false)}
      />

      {/* Members Modal */}
      <AitPropertyMembersModal
        isOpen={showMembers}
        onClose={() => setShowMembers(false)}
        propertyId={id}
        isOwner={property?.user_role === 'owner'}
        currentUserRole={property?.user_role}
        onInviteClick={() => setShowInvite(true)}
      />

      {/* Invite Member Modal */}
      <AitInviteMemberModal
        isOpen={showInvite}
        onClose={() => setShowInvite(false)}
        propertyId={id}
        onInviteSent={() => {}}
      />

      {/* Edit Building Modal */}
      {editBuilding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setEditBuilding(null)}>
          <div className={`mx-4 w-full max-w-sm rounded-lg shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className={`flex items-center justify-between px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="font-medium text-sm">Edit Building</h3>
              <button onClick={() => setEditBuilding(null)} className={`p-1 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                <X size={16} />
              </button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const result = await updateBuilding(id, editBuilding.id, editBuildingForm);
              if (result.success) {
                toast.success('Building updated');
                setEditBuilding(null);
                loadData();
              } else {
                toast.error(result.error || 'Failed to update');
              }
            }} className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1">BIN Number</label>
                <input type="text" value={editBuildingForm.bin_number}
                  onChange={e => setEditBuildingForm(f => ({ ...f, bin_number: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
                  placeholder="e.g., FL0053701" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Building Name</label>
                <input type="text" value={editBuildingForm.name}
                  onChange={e => setEditBuildingForm(f => ({ ...f, name: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}
                  placeholder="Optional name" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Placed in Service Date</label>
                <input type="date" value={editBuildingForm.placed_in_service_date}
                  onChange={e => setEditBuildingForm(f => ({ ...f, placed_in_service_date: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditBuilding(null)}
                  className={`px-3 py-1.5 text-sm rounded-lg ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
                  Cancel
                </button>
                <button type="submit"
                  className="px-4 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <AitLeasingReport
        isOpen={showLeasingReport}
        onClose={() => setShowLeasingReport(false)}
        propertyId={id}
        propertyName={property?.name}
      />

      {/* Documents Modal */}
      {showDocuments && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowDocuments(false)}>
          <div className={`mx-4 w-full max-w-lg max-h-[80vh] flex flex-col rounded-lg shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className={`flex items-center justify-between px-5 py-4 border-b shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className="font-medium">Documents</h3>
              <div className="flex items-center gap-2">
                <label className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg cursor-pointer ${
                  isDark ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-500 text-white hover:bg-blue-600'
                }`}>
                  <Upload size={12} /> Upload
                  <input type="file" className="hidden" accept=".xlsx,.xls,.csv,.pdf" multiple
                    onChange={async (e) => {
                      const files = Array.from(e.target.files || []);
                      let saved = 0;
                      for (const file of files) {
                        const fileType = file.name.toLowerCase().endsWith('.pdf') ? 'tic' : 'rent_roll';
                        const result = await saveUpload(id, file, file.name, fileType);
                        if (result.success && result.upload) {
                          setUploads(prev => [result.upload, ...prev]);
                          saved++;
                        }
                      }
                      if (saved > 0) toast.success(`${saved} document(s) saved`);
                      e.target.value = '';
                    }} />
                </label>
                <button onClick={() => setShowDocuments(false)} className={`p-1 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {uploads.length === 0 ? (
                <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  <FileText size={32} className="mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No documents saved yet</p>
                  <p className="text-xs mt-1">Upload rent rolls, TICs, or other property documents</p>
                </div>
              ) : (
                <div className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {uploads.map(upload => (
                    <div key={upload.id} className={`group flex items-center justify-between px-5 py-3 ${isDark ? 'hover:bg-gray-750' : 'hover:bg-gray-50'}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        {upload.file_type === 'tic'
                          ? <FileText size={16} className="text-purple-500 shrink-0" />
                          : <FileSpreadsheet size={16} className="text-blue-500 shrink-0" />
                        }
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{upload.filename}</p>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {upload.file_type === 'tic' ? 'TIC' : upload.file_type === 'rent_roll' ? 'Rent Roll' : upload.file_type}
                            {' — '}
                            {new Date(upload.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {upload.file_url && (
                          <a
                            href={upload.file_url?.startsWith('http') ? upload.file_url : `${RAILS_ROOT}${upload.file_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`p-1 rounded transition-colors ${isDark ? 'text-blue-400 hover:bg-blue-500/10' : 'text-blue-600 hover:bg-blue-50'}`}
                            title="View / Download"
                          >
                            <ExternalLink size={14} />
                          </a>
                        )}
                        {!upload.file_url && (
                          <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                            No file
                          </span>
                        )}
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Delete "${upload.filename}"?`)) return;
                            const result = await deleteUpload(id, upload.id);
                            if (result.success) {
                              setUploads(prev => prev.filter(u => u.id !== upload.id));
                              toast.success('Document deleted');
                            } else {
                              toast.error(result.error || 'Failed to delete');
                            }
                          }}
                          className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-500 hover:bg-red-500/10"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Error Modal */}
      {uploadError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setUploadError(null)}>
          <div className={`mx-4 w-full max-w-md rounded-lg shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            <div className={`flex items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/30">
                <XCircle size={20} className="text-red-600 dark:text-red-400" />
              </div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{uploadError.title}</h3>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{uploadError.message}</p>
              {uploadError.suggestions?.length > 0 && (
                <div className="space-y-1.5">
                  <p className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>What to try</p>
                  <ul className="space-y-1">
                    {uploadError.suggestions.map((s, i) => (
                      <li key={i} className={`flex items-start gap-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        <CheckCircle2 size={14} className="text-blue-500 shrink-0 mt-0.5" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {uploadError.details && (
                <details className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  <summary className="cursor-pointer hover:underline">Technical details</summary>
                  <pre className={`mt-1 p-2 rounded text-xs overflow-x-auto ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>{uploadError.details}</pre>
                </details>
              )}
            </div>
            <div className={`flex justify-end px-5 py-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setUploadError(null)}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <AitGlossaryModal
        isOpen={!!glossaryTopic}
        topic={glossaryTopic}
        onClose={() => setGlossaryTopic(null)}
      />
    </div>
    </div>
  );
}

export default AitPropertyDetail;
